/**
 * LazyTableCalculator - Async calculation system with caching and progress tracking
 * Provides non-blocking table calculations with intelligent caching and dependency tracking
 */

import { devLog, devError } from '../../../../../utils/devLog';
import { JatakaResponse } from '../../../../../models/api';

export interface LazyTableConfig<T> {
    id: string;
    calculator: (apiResponse: JatakaResponse, onProgress?: (progress: number) => void) => Promise<T>;
    dependencies: string[]; // API fields that trigger recalculation
    cacheKey: (apiResponse: JatakaResponse) => string;
    ttl?: number; // Time to live in milliseconds
    description?: string; // For debugging
}

export interface CachedResult<T> {
    data: T;
    timestamp: number;
    ttl: number;
    cacheKey: string;
    calculationTime: number;
}

export interface CalculationProgress {
    calculatorId: string;
    progress: number; // 0-100
    status: 'pending' | 'calculating' | 'completed' | 'error';
    startTime: number;
    error?: string;
}

/**
 * Main LazyTableCalculator class - manages async calculations with caching
 */
class LazyTableCalculator {
    private cache = new Map<string, CachedResult<any>>();
    private calculations = new Map<string, LazyTableConfig<any>>();
    private activeCalculations = new Map<string, Promise<any>>();
    private progressCallbacks = new Map<string, Set<(progress: CalculationProgress) => void>>();

    /**
     * Register a lazy calculation configuration
     */
    register<T>(config: LazyTableConfig<T>): void {
        this.calculations.set(config.id, config);
        devLog(`⚡ LazyCalculator: Registered "${config.id}" with dependencies [${config.dependencies.join(', ')}]`);
    }

    /**
     * Calculate table data with caching and progress tracking
     */
    async calculate<T>(
        tableId: string,
        apiResponse: JatakaResponse,
        onProgress?: (progress: CalculationProgress) => void
    ): Promise<T> {
        const config = this.calculations.get(tableId) as LazyTableConfig<T>;
        if (!config) {
            throw new Error(`Table calculator "${tableId}" not registered`);
        }

        const cacheKey = `${tableId}:${config.cacheKey(apiResponse)}`;
        const cached = this.cache.get(cacheKey) as CachedResult<T>;

        // Check cache validity
        if (cached && this.isCacheValid(cached)) {
            devLog(`💾 LazyCalculator: Cache hit for "${tableId}" (age: ${Date.now() - cached.timestamp}ms)`);

            if (onProgress) {
                onProgress({
                    calculatorId: tableId,
                    progress: 100,
                    status: 'completed',
                    startTime: cached.timestamp
                });
            }

            return cached.data;
        }

        // Check if calculation is already in progress
        const existingCalculation = this.activeCalculations.get(cacheKey);
        if (existingCalculation) {
            devLog(`🔄 LazyCalculator: Joining existing calculation for "${tableId}"`);
            return existingCalculation;
        }

        // Start new calculation
        const calculationPromise = this.performCalculation(config, apiResponse, cacheKey, onProgress);
        this.activeCalculations.set(cacheKey, calculationPromise);

        try {
            const result = await calculationPromise;
            return result;
        } finally {
            this.activeCalculations.delete(cacheKey);
        }
    }

    /**
     * Perform the actual calculation with progress tracking
     */
    private async performCalculation<T>(
        config: LazyTableConfig<T>,
        apiResponse: JatakaResponse,
        cacheKey: string,
        onProgress?: (progress: CalculationProgress) => void
    ): Promise<T> {
        const startTime = Date.now();

        // Progress tracking
        const updateProgress = (progress: number, status: CalculationProgress['status'] = 'calculating') => {
            const progressData: CalculationProgress = {
                calculatorId: config.id,
                progress,
                status,
                startTime
            };

            if (onProgress) onProgress(progressData);
            this.notifyProgressListeners(config.id, progressData);
        };

        updateProgress(0, 'pending');

        try {
            devLog(`🚀 LazyCalculator: Starting calculation for "${config.id}"`);
            updateProgress(10, 'calculating');

            // Perform calculation with progress callback
            const result = await config.calculator(apiResponse, (calcProgress) => {
                updateProgress(10 + (calcProgress * 0.8)); // Map 0-100 to 10-90
            });

            const calculationTime = Date.now() - startTime;

            // Cache the result
            const cachedResult: CachedResult<T> = {
                data: result,
                timestamp: Date.now(),
                ttl: config.ttl || 300000, // 5 minutes default
                cacheKey,
                calculationTime
            };

            this.cache.set(cacheKey, cachedResult);
            updateProgress(100, 'completed');

            devLog(`✅ LazyCalculator: Completed "${config.id}" in ${calculationTime}ms`);

            return result;

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            devError(`❌ LazyCalculator: Error in "${config.id}":`, error);

            updateProgress(0, 'error');
            this.notifyProgressListeners(config.id, {
                calculatorId: config.id,
                progress: 0,
                status: 'error',
                startTime,
                error: errorMessage
            });

            throw error;
        }
    }

    /**
     * Check if cached result is still valid
     */
    private isCacheValid<T>(cached: CachedResult<T>): boolean {
        return (Date.now() - cached.timestamp) < cached.ttl;
    }

    /**
     * Subscribe to calculation progress for a specific calculator
     */
    onProgress(calculatorId: string, callback: (progress: CalculationProgress) => void): () => void {
        if (!this.progressCallbacks.has(calculatorId)) {
            this.progressCallbacks.set(calculatorId, new Set());
        }

        this.progressCallbacks.get(calculatorId)!.add(callback);

        // Return unsubscribe function
        return () => {
            this.progressCallbacks.get(calculatorId)?.delete(callback);
        };
    }

    /**
     * Notify all progress listeners for a calculator
     */
    private notifyProgressListeners(calculatorId: string, progress: CalculationProgress): void {
        const listeners = this.progressCallbacks.get(calculatorId);
        listeners?.forEach(callback => {
            try {
                callback(progress);
            } catch (error) {
                devError(`❌ LazyCalculator: Progress listener error for ${calculatorId}:`, error);
            }
        });
    }

    /**
     * Clear cache for specific calculator or all calculators
     */
    clearCache(calculatorId?: string): void {
        if (calculatorId) {
            const config = this.calculations.get(calculatorId);
            if (config) {
                // Clear all cache entries for this calculator
                const keysToDelete: string[] = [];
                for (const [key] of this.cache) {
                    if (key.startsWith(`${calculatorId}:`)) {
                        keysToDelete.push(key);
                    }
                }
                keysToDelete.forEach(key => this.cache.delete(key));
                devLog(`🗑️ LazyCalculator: Cleared cache for "${calculatorId}" (${keysToDelete.length} entries)`);
            }
        } else {
            this.cache.clear();
            devLog(`🗑️ LazyCalculator: Cleared all cache`);
        }
    }

    /**
     * Check if calculation is currently in progress
     */
    isCalculating(tableId: string, apiResponse: JatakaResponse): boolean {
        const config = this.calculations.get(tableId);
        if (!config) return false;

        const cacheKey = `${tableId}:${config.cacheKey(apiResponse)}`;
        return this.activeCalculations.has(cacheKey);
    }

    /**
     * Get cache statistics
     */
    getCacheStats(): Record<string, any> {
        const stats = {
            totalEntries: this.cache.size,
            activeCalculations: this.activeCalculations.size,
            entries: [] as any[]
        };

        for (const [key, cached] of this.cache) {
            stats.entries.push({
                key,
                age: Date.now() - cached.timestamp,
                ttl: cached.ttl,
                calculationTime: cached.calculationTime,
                isValid: this.isCacheValid(cached)
            });
        }

        return stats;
    }

    /**
     * Get debug information
     */
    getDebugInfo(): Record<string, any> {
        return {
            registeredCalculators: Array.from(this.calculations.keys()),
            cacheStats: this.getCacheStats(),
            activeProgressListeners: Object.fromEntries(
                Array.from(this.progressCallbacks.entries()).map(([id, listeners]) => [id, listeners.size])
            )
        };
    }
}

// Singleton instance
export const lazyCalculator = new LazyTableCalculator();

export default lazyCalculator;